import React, { useState, useEffect } from 'react';

export default function Products({ 
  filteredAssets = [], 
  searchQuery, 
  setSearchQuery, 
  selectedCategory,      
  setSelectedCategory,  
  categories = [],       
  handleProductClick, 
  addToCart, 
  cart = [], 
  handleSingleInquiryClick, 
  setCurrentPage 
}) {
  // Mobile Viewport Tracking Engine
  const [isMobile, setIsMobile] = useState(false);
  const [addedItems, setAddedItems] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🛒 Enhanced add to cart handler with animation feedback
  const handleAddToCart = (asset, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // If already in cart, show flash feedback
    if (cart.some(item => item.id === asset.id)) {
      setAddedItems(prev => ({ ...prev, [asset.id]: 'flash' }));
      setTimeout(() => {
        setAddedItems(prev => ({ ...prev, [asset.id]: undefined }));
      }, 500);
      return;
    }
    
    // Get the button element for animation reference
    let sourceElement = null;
    if (event && event.currentTarget) {
      sourceElement = event.currentTarget;
    } else if (event && event.target) {
      sourceElement = event.target.closest('button') || event.target;
    }
    
    // Call parent addToCart with the asset and event for animation
    addToCart(asset, event || sourceElement);
    
    // Show "Added!" state
    setAddedItems(prev => ({ ...prev, [asset.id]: 'added' }));
    setTimeout(() => {
      setAddedItems(prev => ({ ...prev, [asset.id]: undefined }));
    }, 1500);
  };

  // FRONTEND-ONLY ENGINE: Extract unique categories dynamically if none provided
  const displayCategories = categories.length > 0 
    ? categories 
    : [...new Set(filteredAssets.map(asset => asset?.category))].filter(Boolean);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh', 
      fontFamily: '"Inter", sans-serif',
      backgroundColor: '#f8fafc'
    }}>
      
      {/* Main Content Wrapper */}
      <div style={{ flex: '1', padding: isMobile ? '20px 12px' : '40px 20px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '20px' : '40px', flexWrap: 'wrap', gap: '16px' }}>
          
          {/* Header Title Block */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isMobile && (
              <button 
                onClick={() => setCurrentPage('home')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px 4px 0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Back to Home"
                title="Back to Home"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: isMobile ? '1.4rem' : '1.75rem', fontWeight: '800' }}>Active Inventory Catalog</h2>
          </div>

          {/* Search & Filter Controls Group */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            width: isMobile ? '100%' : 'auto', 
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            flex: isMobile ? 'none' : '1',
            justifyContent: 'flex-end',
            maxWidth: isMobile ? '100%' : '600px'
          }}>
            {/* Category Dropdown Menu */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '24px',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                outline: 'none',
                fontWeight: '600',
                fontSize: '0.9rem',
                cursor: 'pointer',
                boxSizing: 'border-box',
                width: isMobile ? '100%' : '180px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <option value="All">All Categories</option>
              {displayCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            {/* Search input field */}
            <input 
              type="text" 
              placeholder="🔍 Search products..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              style={{ 
                padding: '12px 20px', 
                width: '100%', 
                maxWidth: isMobile ? '100%' : '350px', 
                border: '1px solid #e2e8f0', 
                borderRadius: '24px', 
                outline: 'none', 
                boxSizing: 'border-box' 
              }} 
            />
          </div>
        </div>

        {/* Product Layout Grid Wrapper */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: isMobile ? '12px' : '24px',
          width: '100%'
        }}>
          {filteredAssets.map((asset) => {
            if (!asset) return null;
            const isInCart = cart.some(item => item?.id === asset.id);
            const buttonState = addedItems[asset.id];
            
            return (
              <div 
                key={asset.id} 
                onClick={() => handleProductClick(asset)} 
                className="product-card"
                style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'row' : 'column', 
                  width: '100%',
                  height: isMobile ? '130px' : '100%', 
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  border: '1px solid #e2e8f0', 
                  cursor: 'pointer', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.03)';
                  }
                }}
              >
                {/* Product Image Container */}
                <div style={{ 
                  width: isMobile ? '120px' : '100%', 
                  height: isMobile ? '100%' : '200px', 
                  flexShrink: 0,
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: isMobile ? 'none' : '1px solid #f1f5f9',
                  position: 'relative'
                }}>
                  <img 
                    src={asset.image} 
                    alt={asset.name || 'Product Image'} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain', 
                      padding: isMobile ? '8px' : '12px', 
                      boxSizing: 'border-box' 
                    }} 
                  />
                  
                  {/* In Cart Badge */}
                  {isInCart && (
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: '#22c55e',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.55rem',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      animation: 'fadeIn 0.3s ease',
                      boxShadow: '0 2px 8px rgba(34,197,94,0.3)'
                    }}>
                      ✓ In Cart
                    </div>
                  )}
                </div>
                
                {/* Information & Actions Layout Body */}
                <div style={{ 
                  padding: isMobile ? '12px' : '16px', 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  gap: '8px',
                  minWidth: 0 
                }}>
                  
                  {/* Text Descriptions Wrapper */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    minWidth: 0 
                  }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                      {asset.category}
                    </span>
                    <h4 style={{ 
                      margin: 0, 
                      fontSize: isMobile ? '0.85rem' : '1.05rem', 
                      fontWeight: '700', 
                      lineHeight: '1.3',
                      color: '#0f172a',
                      display: '-webkit-box',
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      height: isMobile ? 'auto' : '2.6em'
                    }}>
                      {asset.name}
                    </h4>
                  </div>

                  {/* Buttons Action Wrapper */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: '8px',
                    width: '100%',
                    marginTop: 'auto'
                  }}>
                    {/* 🛒 ADD TO CART BUTTON WITH ANIMATION */}
                    <button 
                      onClick={(e) => handleAddToCart(asset, e)}
                      style={{ 
                        padding: isMobile ? '8px 4px' : '10px 8px', 
                        backgroundColor: isInCart ? '#22c55e' : (buttonState === 'added' ? '#22c55e' : '#fbff00'),
                        color: isInCart ? '#ffffff' : (buttonState === 'added' ? '#ffffff' : '#0f172a'),
                        border: 'none', 
                        borderRadius: '6px', 
                        fontWeight: '700', 
                        fontSize: '0.72rem', 
                        cursor: isInCart ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        transition: 'all 0.3s ease',
                        transform: buttonState === 'added' ? 'scale(0.95)' : 'scale(1)',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: isInCart ? '0 2px 8px rgba(34,197,94,0.3)' : 'none'
                      }}
                    >
                      {isInCart ? '✓ Added' : (buttonState === 'added' ? '✓ Added!' : '➕ Add')}
                      
                      {/* Ripple effect for added items */}
                      {buttonState === 'added' && (
                        <span style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: '0',
                          height: '0',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.4)',
                          transform: 'translate(-50%, -50%)',
                          animation: 'ripple 0.6s ease-out forwards'
                        }} />
                      )}
                    </button>
                    
                    {/* INQUIRE BUTTON */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleSingleInquiryClick(asset); }} 
                      style={{ 
                        padding: isMobile ? '8px 4px' : '10px 8px', 
                        backgroundColor: '#ea4335', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '0.72rem', 
                        fontWeight: '700', 
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(234,67,53,0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#d33426';
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ea4335';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      Inquire
                    </button>
                  </div>

                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredAssets.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            marginTop: '20px'
          }}>
            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>🔍</span>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', fontWeight: '700' }}>No Products Found</h3>
            <p style={{ margin: 0, color: '#64748b' }}>
              Try adjusting your search or filter criteria
            </p>
            <button 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
              style={{
                marginTop: '16px',
                padding: '8px 20px',
                backgroundColor: '#0284c7',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* ANIMATION STYLES */}
      <style>{`
        @keyframes ripple {
          0% {
            width: 0;
            height: 0;
            opacity: 1;
          }
          100% {
            width: 200px;
            height: 200px;
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .product-card {
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

    </div>
  );
}
